import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONSENT_STORAGE_KEY,
  getStoredConsent,
  setConsentDefault,
  updateConsentGranted,
  updateConsentDenied,
} from '../../lib/gtag';
import type { ConsentStatus } from '../../lib/gtag';

export default function CookieBanner() {
  const [status, setStatus] = useState<ConsentStatus>('unset');

  useEffect(() => {
    setConsentDefault();

    const stored = getStoredConsent();
    setStatus(stored);

    if (stored === 'accepted') {
      updateConsentGranted();
    }

    if (stored === 'rejected') {
      updateConsentDenied();
    }

    const openSettings = () => {
      setStatus('unset');
    };

    window.addEventListener('open-cookie-settings', openSettings);

    return () => {
      window.removeEventListener('open-cookie-settings', openSettings);
    };
  }, []);

  const handleAccept = () => {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'accepted');
    setStatus('accepted');
    updateConsentGranted();
    window.dispatchEvent(new CustomEvent('consent-updated'));
  };

  const handleReject = () => {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'rejected');
    setStatus('rejected');
    updateConsentDenied();
    window.dispatchEvent(new CustomEvent('consent-updated'));
  };

  if (status === 'unset') {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#06111F]/75 backdrop-blur-md px-3 sm:px-4">
        <div className="w-full sm:max-w-2xl mb-3 sm:mb-0 rounded-[28px] border border-[#BFDBFE] bg-white/95 backdrop-blur-2xl shadow-[0_28px_90px_rgba(11,31,58,0.35)] p-5 sm:p-7">
          <div className="flex items-start gap-3 sm:gap-4 mb-4">
            <div className="shrink-0 w-11 h-11 rounded-2xl bg-[#38BDF8]/10 border border-[#38BDF8]/25 flex items-center justify-center">
              <i className="ri-shield-check-line text-xl text-[#123E7A]" />
            </div>
            <div>
              <h2 className="text-base sm:text-xl font-semibold text-[#0B1F3A] leading-tight">
                Cookies & Datenschutz
              </h2>
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#123E7A]/75 mt-1">
                Sicher. Transparent. Nur mit Zustimmung.
              </p>
            </div>
          </div>

          <p className="text-xs sm:text-sm leading-relaxed text-[#1E3A5F] mb-5">
            Wir verwenden technisch notwendige Cookies für den Betrieb der Website. Marketing- und Tracking-Dienste wie Google Ads werden nur nach deiner ausdrücklichen Einwilligung aktiviert. Details findest du in unserer{' '}
            <Link
              to="/datenschutz"
              className="font-semibold underline underline-offset-4 text-[#123E7A] hover:text-[#0B1F3A]"
            >
              Datenschutzerklärung
            </Link>
            .
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleReject}
              className="w-full px-4 py-3 rounded-2xl text-xs sm:text-sm font-medium border border-[#BFDBFE] bg-[#E0F2FE] text-[#0B1F3A] hover:bg-[#BAE6FD] transition-colors"
            >
              Nur notwendige Cookies
            </button>
            <button
              type="button"
              onClick={handleAccept}
              className="w-full px-4 py-3 rounded-2xl text-xs sm:text-sm font-medium bg-[#0B1F3A] text-white shadow-[0_18px_40px_rgba(18,62,122,0.28)] hover:bg-[#123E7A] transition-colors"
            >
              Alle akzeptieren
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setStatus('unset')}
      className="fixed bottom-4 right-4 z-[9998] flex items-center justify-center w-12 h-12 rounded-2xl border border-[#7DD3FC] bg-[#0B1F3A] text-white shadow-[0_16px_40px_rgba(11,31,58,0.35)] hover:bg-[#123E7A] transition-colors"
      aria-label="Cookie-Einstellungen öffnen"
    >
      <i className="ri-shield-keyhole-line text-xl text-[#E0F2FE]" />
    </button>
  );
}
